import React, { useEffect, useMemo, useState } from "react";
import { toast } from "../../components/AppToast";
import PackingSearchResults from "./PackingSearchResults";
import ConfirmDialog from "../../components/ConfirmDialog";
import CompleteActionsModal from "./CompleteActionsModal";

export default function OrderDetails({
    header,
    tableData,
    scannedSet,            // not used anymore, kept for compat
    totalLines,            // legacy (lines), now we use totalQty
    onScanSuccess = () => { },
    onBack = () => { },
    canPartial = false,
    canComplete = false,
}) {
    const [scanValue, setScanValue] = useState("");

    // Build meta per SKU
    const skuMeta = useMemo(() => {
        const map = new Map();
        (Object.entries(tableData || {})
            .filter(([k]) => !k.startsWith("__"))
            .flatMap(([, arr]) => (Array.isArray(arr) ? arr : [])) || []
        ).forEach((row) => {
            const sku = String(row.skuId ?? row.item ?? "").trim();
            if (!sku) return;
            const expected = Number(row.expectedQty ?? row.orderedQty ?? 0) || 0;
            const initialPacked = Number(row.packedQty ?? 0) || 0;
            map.set(sku, { expected, initialPacked });
        });
        return map;
    }, [tableData]);

    // total expected quantity across all items
    const totalQty = useMemo(() => {
        let sum = 0;
        skuMeta.forEach(({ expected }) => (sum += expected));
        return sum;
    }, [skuMeta]);

    // live packed quantities
    const [packedBySku, setPackedBySku] = useState({});
    useEffect(() => {
        const init = {};
        skuMeta.forEach((meta, sku) => {
            init[sku] = Math.max(0, Math.min(meta.expected, meta.initialPacked || 0));
        });
        setPackedBySku(init);
    }, [skuMeta]);

    // total scanned quantity (sum of packedBySku)
    const scannedQty = useMemo(
        () => Object.values(packedBySku).reduce((a, v) => a + (Number(v) || 0), 0),
        [packedBySku]
    );

    // dialogs
    const [showNotInOrder, setShowNotInOrder] = useState(false);
    const [badSku, setBadSku] = useState("");

    const [showAlreadyScanned, setShowAlreadyScanned] = useState(false);
    const [fullSku, setFullSku] = useState("");

    // complete actions modal
    const [showCompleteActions, setShowCompleteActions] = useState(false);

    const handleScanEnter = () => {
        const sku = scanValue.trim();
        if (!sku) return;

        // SKU not part of order
        if (!skuMeta.has(sku)) {
            setBadSku(sku);
            setShowNotInOrder(true);
            setScanValue("");
            return;
        }

        const { expected } = skuMeta.get(sku);
        const current = Number(packedBySku[sku] ?? 0);

        // overall total quantity cap
        if (scannedQty + 1 > totalQty) {
            toast.warning("Can’t scan more than total item quantity");
            setScanValue("");
            return;
        }

        // per-SKU cap
        if (current >= expected) {
            setFullSku(sku);
            setShowAlreadyScanned(true); // use dialog instead of toast
            setScanValue("");
            return;
        }

        // apply +1 to this SKU
        setPackedBySku((prev) => ({
            ...prev,
            [sku]: Math.min(expected, (Number(prev[sku]) || 0) + 1),
        }));

        onScanSuccess(sku);
        // message wording: "scanned", not "scan"
        toast.success(`SKU ${sku} scanned`);
        setScanValue("");
    };

    // complete modal actions (stub)
    const handlePrintShipping = () => {
        setShowCompleteActions(false);
        toast.info("Printing shipping label…");
    };
    const handlePrintInvoice = () => {
        setShowCompleteActions(false);
        toast.info("Printing invoice label…");
    };
    const handleConfirmPackSeal = () => {
        setShowCompleteActions(false);
        toast.success("Pack & Seal confirmed");
    };

    return (
        <>
            <div className="pb-5">
                {/* TOP: two equal-height boxes */}
                <div className="row gx-3 align-items-stretch">
                    {/* LEFT: order details */}
                    <div className="col-lg-8 d-flex">
                        <div className="border rounded-3 p-3 mb-3 mb-lg-0 w-100 h-70 d-flex flex-column bg-light">
                            <h6 className="mb-3">Order Details</h6>
                            {header ? (
                                <div className="row g-3 small">
                                    <div className="col-md-6"><div><strong>Order ID:</strong> {header.orderId}</div></div>
                                    <div className="col-md-6"><div><strong>Status:</strong> {header.status}</div></div>
                                    <div className="col-md-6"><div><strong>Customer:</strong> {header.customer}</div></div>
                                    <div className="col-md-6"><div><strong>Order Date:</strong> {header.orderDate}</div></div>
                                    <div className="col-md-12"><div><strong>Delivery Address:</strong> {header.deliveryAddress}</div></div>
                                    <div className="col-md-6"><div><strong>Expected Delivery:</strong> {header.expectedDate}</div></div>
                                </div>
                            ) : (
                                <div className="text-muted small">No header data</div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: scanner */}
                    <div className="col-lg-4 d-flex">
                        <div className="border rounded-3 p-3 w-100 h-100 d-flex flex-column">
                            <h6 className="mb-3 text-center">Scan Items</h6>

                            <div className="input-group mb-2">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Scan / Enter SKU ID"
                                    value={scanValue}
                                    onChange={(e) => setScanValue(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && handleScanEnter()}
                                />
                                <button className="btn btn-primary" type="button" onClick={handleScanEnter}>
                                    Enter
                                </button>
                            </div>

                            {/* Quantity counter: scanned/total (quantities, not lines) */}
                            <div className="text-end small text-muted mt-auto">
                                {scannedQty}/{totalQty} qty scanned
                            </div>

                            <hr className="mt-3 mb-0" />
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                <div className="mt-3">
                    <div className="border rounded-3 p-2">
                        <PackingSearchResults
                            searchResults={tableData}
                            packedBySku={packedBySku}
                        />
                    </div>
                </div>
            </div>

            {/* Footer actions */}
            <div className="mt-3">
                <div className="d-flex justify-content-center gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={onBack}>
                        Back
                    </button>
                    <button type="button" className="btn btn-warning" disabled={!canPartial} onClick={() => setShowCompleteActions(true)}>
                        Partial Dispatch
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!canComplete}
                        onClick={() => setShowCompleteActions(true)}
                    >
                        Complete Order
                    </button>
                </div>
            </div>

            {/* Alerts */}
            <ConfirmDialog
                open={showNotInOrder}
                onClose={() => setShowNotInOrder(false)}
                onConfirm={() => setShowNotInOrder(false)}
                title="Alert"
                message={`Item scan not part of the order${badSku ? ` (SKU: ${badSku})` : ""}`}
                yesText="OK"
                noText=""
            />

            <ConfirmDialog
                open={showAlreadyScanned}
                onClose={() => setShowAlreadyScanned(false)}
                onConfirm={() => setShowAlreadyScanned(false)}
                title="Alert"
                message={`This SKU is already scanned${fullSku ? ` (SKU: ${fullSku})` : ""}`}
                yesText="OK"
                noText=""
            />

            {/* Complete Actions modal */}
            <CompleteActionsModal
                open={showCompleteActions}
                onClose={() => setShowCompleteActions(false)}
                onPrintShipping={handlePrintShipping}
                onPrintInvoice={handlePrintInvoice}
                onConfirmPackSeal={handleConfirmPackSeal}
            />
        </>
    );
}
